import {
  catchError,
  concatMap,
  distinctUntilChanged,
  EMPTY,
  Observable,
  takeWhile,
  timeout,
  timer,
} from 'rxjs';

const DEFAULT_POLL_INTERVAL_MS = 2000;
// `each` measures the gap between distinct emissions, not total elapsed time,
// so a healthy import that keeps reporting Processing never trips this; only
// a stuck backend does. Number kept in parity with the previous
// MAX_POLL_ATTEMPTS × interval budget; agree with product for the real value.
const DEFAULT_POLL_DEADLINE_MS = 300_000;

export interface PollUntilTerminalConfig<TData, TMessage> {
  /**
   * Called on every tick. Must be a self-contained observable that emits
   * exactly one value or errors — pass an HTTP client method, not something
   * routed through the NgRx action stream (races on shared action types can
   * silently cancel).
   */
  fetch: () => Observable<TData>;
  /**
   * Convert a response into a progress message stream. Return `of(msg)` for
   * a normal emission, or `throwError(...)` to error the observable when the
   * message represents a terminal failure whose consumer reads via the
   * subscriber's `error` handler.
   */
  toMessage: (data: TData) => Observable<TMessage>;
  /** Stop polling once a message satisfies this predicate. */
  isTerminal: (message: TMessage) => boolean;
  /**
   * Discriminator for `distinctUntilChanged`. Defaults to reference equality;
   * pass a getter (e.g. `msg => msg.event`) so identical status responses
   * emit at most once.
   */
  eventKey?: (message: TMessage) => unknown;
  /**
   * Watchdog observable — subscribed to when no new distinct message arrives
   * within `deadlineMs`. Use `throwError(...)` if the consumer's `error`
   * handler should read the timeout reason, or `of(terminalMessage)` if it
   * reads via `next` (WS parity dictates which).
   */
  onDeadline: () => Observable<TMessage>;
  intervalMs?: number;
  deadlineMs?: number;
}

export function pollUntilTerminal<TData, TMessage>({
  fetch,
  toMessage,
  isTerminal,
  eventKey = message => message,
  onDeadline,
  intervalMs = DEFAULT_POLL_INTERVAL_MS,
  deadlineMs = DEFAULT_POLL_DEADLINE_MS,
}: PollUntilTerminalConfig<TData, TMessage>): Observable<TMessage> {
  return timer(0, intervalMs).pipe(
    // concatMap serializes requests (no overlap) and cancels the in-flight
    // GET when the outer observable is unsubscribed — no manual bookkeeping.
    concatMap(() => fetch().pipe(catchError(() => EMPTY))),
    concatMap(data => toMessage(data)),
    distinctUntilChanged((prev, next) => eventKey(prev) === eventKey(next)),
    // `true` makes takeWhile inclusive — the terminal message is emitted
    // before completion.
    takeWhile(message => !isTerminal(message), true),
    timeout({ each: deadlineMs, with: onDeadline }),
  );
}
